import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
  Divider
} from '@mui/material';
import { Clock, Check, X, HelpCircle } from 'lucide-react';

/**
 * Modal pour saisir le temps réel lors du passage d'un item à "Terminé"
 * 
 * Étape 1: Question "Voulez-vous entrer le temps réel ?"
 * Étape 2: Saisie des heures réelles par type
 */
function ActualHoursModal({ open, onClose, item, onSave }) {
  const [step, setStep] = useState(1);
  const [actualHours, setActualHours] = useState({});
  const [enabledTypes, setEnabledTypes] = useState({});

  // Initialize state when item changes
  useEffect(() => {
    if (item?.hours && open) {
      const initial = {};
      const enabled = {};
      
      item.hours.forEach(h => {
        if (h.hours > 0) {
          // Pre-fill with estimated hours
          initial[h.hourTypeId] = h.hours;
          enabled[h.hourTypeId] = true;
        }
      });
      
      setActualHours(initial);
      setEnabledTypes(enabled);
      setStep(1);
    }
  }, [item, open]);

  // Get hour types with hours > 0
  const hourTypesWithHours = item?.hours?.filter(h => h.hours > 0) || [];

  const handleClose = (saveStatus = false) => {
    // Reset state
    setStep(1);
    setActualHours({});
    setEnabledTypes({});
    
    if (saveStatus) {
      // Just change status to DONE without actual hours
      onSave({ status: 'DONE' });
    }
    onClose();
  };

  const handleNo = () => {
    // Change status to DONE without actual hours
    handleClose(true);
  };

  const handleYes = () => {
    setStep(2);
  };

  const handleToggleType = (hourTypeId) => {
    setEnabledTypes(prev => ({
      ...prev,
      [hourTypeId]: !prev[hourTypeId]
    }));
  };

  const handleHoursChange = (hourTypeId, value) => {
    const numValue = parseFloat(value) || 0;
    setActualHours(prev => ({
      ...prev,
      [hourTypeId]: numValue >= 0 ? numValue : 0
    }));
  };

  const handleOk = () => {
    // Build actual hours object with only enabled types
    const finalActualHours = {};
    Object.entries(enabledTypes).forEach(([hourTypeId, isEnabled]) => {
      if (isEnabled && actualHours[hourTypeId] !== undefined) {
        finalActualHours[hourTypeId] = actualHours[hourTypeId];
      }
    });
    
    onSave({ 
      status: 'DONE',
      actualHours: finalActualHours
    });
    handleClose(false);
  };

  const handleCancel = () => {
    handleClose(false);
  };

  // Calculate totals
  const totalEstimated = hourTypesWithHours.reduce((sum, h) => sum + (h.hours || 0), 0);
  const totalActual = Object.entries(actualHours)
    .filter(([id]) => enabledTypes[id])
    .reduce((sum, [_, h]) => sum + (h || 0), 0);

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          backgroundImage: 'none'
        }
      }}
    >
      {step === 1 ? (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HelpCircle size={22} color="#00d4aa" />
            <span>Tâche terminée</span>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body1">
                Voulez-vous entrer le temps réel utilisé pour cette tâche ?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ces données serviront à améliorer les estimations futures.
              </Typography>
              
              <Box sx={{ 
                p: 2, 
                bgcolor: 'rgba(255, 255, 255, 0.03)', 
                borderRadius: 1,
                border: '1px solid rgba(255, 255, 255, 0.06)'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {item?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Estimation: {totalEstimated}h
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleNo} 
              variant="outlined"
              color="inherit"
              startIcon={<X size={16} />}
            >
              Non
            </Button>
            <Button 
              onClick={handleYes} 
              variant="contained"
              startIcon={<Check size={16} />}
            >
              Oui
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Clock size={22} color="#00d4aa" />
            <span>Temps réel</span>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Cochez les types d'heures à enregistrer et ajustez le temps réel.
              </Typography>
              
              <Divider sx={{ opacity: 0.1 }} />
              
              {hourTypesWithHours.map(h => (
                <Box 
                  key={h.hourTypeId}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 1.5,
                    bgcolor: enabledTypes[h.hourTypeId] ? 'rgba(0, 212, 170, 0.05)' : 'rgba(255, 255, 255, 0.02)',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: enabledTypes[h.hourTypeId] ? 'rgba(0, 212, 170, 0.2)' : 'rgba(255, 255, 255, 0.06)',
                    transition: 'all 0.2s'
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={enabledTypes[h.hourTypeId] || false}
                        onChange={() => handleToggleType(h.hourTypeId)}
                        sx={{ 
                          color: '#00d4aa',
                          '&.Mui-checked': { color: '#00d4aa' }
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {h.hourType?.name || 'Type inconnu'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Estimé: {h.hours}h
                        </Typography>
                      </Box>
                    }
                    sx={{ flexGrow: 1, m: 0 }}
                  />
                  
                  <TextField
                    type="number"
                    size="small"
                    value={actualHours[h.hourTypeId] || 0}
                    onChange={(e) => handleHoursChange(h.hourTypeId, e.target.value)}
                    disabled={!enabledTypes[h.hourTypeId]}
                    inputProps={{ min: 0, step: 0.5 }}
                    sx={{ width: 100 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">h</InputAdornment>
                    }}
                  />
                </Box>
              ))}
              
              {hourTypesWithHours.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  Aucune heure estimée pour cet item.
                </Typography>
              )}
              
              <Divider sx={{ opacity: 0.1 }} />
              
              {/* Summary */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                p: 2,
                bgcolor: 'rgba(0, 212, 170, 0.08)',
                borderRadius: 1
              }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Estimé</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{totalEstimated}h</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Réel</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#00d4aa' }}>{totalActual}h</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Écart</Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: totalActual > totalEstimated ? '#ef4444' : totalActual < totalEstimated ? '#10b981' : '#9ca3af'
                    }}
                  >
                    {totalActual - totalEstimated >= 0 ? '+' : ''}{totalActual - totalEstimated}h
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleCancel} 
              variant="outlined"
              color="inherit"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleOk} 
              variant="contained"
              disabled={Object.values(enabledTypes).every(v => !v)}
            >
              Enregistrer
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default ActualHoursModal;
